import React, { useEffect, useRef, useState } from 'react';
import DatePicker from '../DatePicker';
import 'react-datepicker/dist/react-datepicker.css';
import { format } from 'date-fns';
import { ChevronDown, Filter as FilterIcon, X } from 'lucide-react';
import { DeviceSearchFacet } from '../../types';
import {
  COLUMN_REGISTRY,
  ColumnDef,
  FILTERABLE_BY_FILTER_KEY,
  GROUP_ORDER,
  filterKeyOf,
} from './columnRegistry';
import { joinRange, splitRange } from './searchParamCodec';
import { useDebouncedValue } from '../../hooks/useDebouncedValue';
import Popover from './Popover';
import Button from '../ui/Button';
import { Checkbox } from '../pilot-ui/checkbox';
import { Input } from '../pilot-ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../pilot-ui/select';

/**
 * Plan 101 — typed filter framework for the Fleet Explorer.
 *
 * A funnel icon opens a checklist of every filterable registry column
 * (grouped like the column chooser). Checking one adds an active-filter row
 * above the table with a type-appropriate editor:
 *   text → debounced substring (ILIKE), boolean → Yes/No select,
 *   enum/prefix → multi-select checklist fed by the server facet option lists
 *   (each computed with its own filter excluded), number → Min/Max pair,
 *   date → From/To pickers. Values travel as `f.<key>` URL params.
 */

const FILTERABLE_COLUMNS = COLUMN_REGISTRY.filter((c) => c.filterable);
const ANY_BOOLEAN_VALUE = '__any__';

/**
 * How long a text or number editor waits after the last keystroke before it
 * writes its value. FS-889's Save waits this long after blurring the editors
 * so a value still being typed is in the view it captures.
 */
export const FILTER_DEBOUNCE_MS = 300;

/** The funnel-icon trigger + grouped filterable-column checklist popover. */
export const FilterPicker: React.FC<{
  activeKeys: string[];
  onPick: (filterKey: string, active: boolean) => void;
}> = ({ activeKeys, onPick }) => {
  const [pickerOpen, setPickerOpen] = useState(false);
  const activeSet = new Set(activeKeys);

  return (
    <Popover
      open={pickerOpen}
      onClose={() => setPickerOpen(false)}
      align="right"
      label="Add filters"
      panelClassName="w-72 max-h-96 overflow-y-auto p-3"
      trigger={
        <Button
          variant="secondary"
          onClick={() => setPickerOpen((o) => !o)}
          className={activeKeys.length > 0 || pickerOpen ? 'bg-accent text-accent-foreground hover:bg-accent/90' : undefined}
          aria-label="Add filters"
          aria-expanded={pickerOpen}
          title="Filter columns"
        >
          <FilterIcon size={18} aria-hidden />
        </Button>
      }
    >
      <div className="text-sm font-semibold mb-2">Filter by</div>
      {GROUP_ORDER.map((group) => {
        const cols = FILTERABLE_COLUMNS.filter((c) => c.group === group);
        if (cols.length === 0) return null;
        return (
          <div key={group} className="mb-2">
            <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-1">
              {group}
            </h4>
            <ul>
              {cols.map((col) => {
                const fk = filterKeyOf(col);
                return (
                  <li key={fk}>
                    <label className="flex items-center gap-2 text-sm px-1 py-0.5 rounded-sm hover:bg-gray-50 cursor-pointer">
                      <Checkbox
                        checked={activeSet.has(fk)}
                        onCheckedChange={(checked) => onPick(fk, checked === true)}
                        aria-label={`Filter by ${col.label}`}
                        title={`Add or remove the ${col.label} filter`}
                      />
                      <span className="truncate">{col.label}</span>
                    </label>
                  </li>
                );
              })}
            </ul>
          </div>
        );
      })}
    </Popover>
  );
};

/** The active-filter row above the table: one typed editor per active key. */
export const ActiveFilterRow: React.FC<{
  activeKeys: string[];
  values: Record<string, string>;
  facets: Record<string, DeviceSearchFacet[]>;
  onChange: (filterKey: string, value: string) => void;
  onRemove: (filterKey: string) => void;
  onClearAll: () => void;
}> = ({ activeKeys, values, facets, onChange, onRemove, onClearAll }) => {
  if (activeKeys.length === 0) return null;
  return (
    <div className="mt-3 flex flex-wrap items-center gap-2">
      {activeKeys.map((fk) => {
        const col = FILTERABLE_BY_FILTER_KEY[fk];
        if (!col) return null;
        return (
          <span
            key={fk}
            data-filter-key={fk}
            className="inline-flex items-center gap-1.5 pl-2 pr-1 py-1 rounded-sm border bg-blue-50 border-status-info-border text-sm"
          >
            <span className="text-blue-900 font-medium">{col.label}</span>
            <FilterEditor
              col={col}
              value={values[fk] ?? ''}
              options={col.facet ? facets[col.facet] ?? [] : []}
              onChange={(v) => onChange(fk, v)}
            />
            <Button
              variant="icon"
              size="sm"
              onClick={() => onRemove(fk)}
              aria-label={`Remove ${col.label} filter`}
              title={`Stop filtering on ${col.label}; the list shows those devices again`}
            >
              <X size={14} aria-hidden />
            </Button>
          </span>
        );
      })}
      <Button
        variant="link"
        size="sm"
        onClick={onClearAll}
        className="ml-1"
        title="Remove every filter above and show all devices again"
      >
        Clear all
      </Button>
    </div>
  );
};

const FilterEditor: React.FC<{
  col: ColumnDef;
  value: string;
  options: DeviceSearchFacet[];
  onChange: (value: string) => void;
}> = ({ col, value, options, onChange }) => {
  switch (col.type) {
    case 'text':
      return <TextEditor value={value} onChange={onChange} label={col.label} />;
    case 'boolean':
      return (
        <Select value={value || ANY_BOOLEAN_VALUE} onValueChange={(next) => onChange(next === ANY_BOOLEAN_VALUE ? '' : next ?? '')}>
          <SelectTrigger className="min-w-32" aria-label={`${col.label} value`} title={`Choose which ${col.label} values to include`}>
            <SelectValue>{value === 'true' ? 'Yes' : value === 'false' ? 'No' : '(any)'}</SelectValue>
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ANY_BOOLEAN_VALUE}>(any)</SelectItem>
            <SelectItem value="true">Yes</SelectItem>
            <SelectItem value="false">No</SelectItem>
          </SelectContent>
        </Select>
      );
    case 'enum':
    case 'prefix':
      return (
        <MultiSelectEditor
          label={col.label}
          value={value}
          options={options}
          onChange={onChange}
          optionLabel={col.optionLabel}
        />
      );
    case 'number':
      return <NumberRangeEditor label={col.label} value={value} onChange={onChange} />;
    case 'date':
      return <DateRangeEditor label={col.label} value={value} onChange={onChange} />;
    default:
      return null;
  }
};

/** Debounced substring editor (matches the free-text search UX). */
const TextEditor: React.FC<{ value: string; onChange: (v: string) => void; label: string }> = ({
  value,
  onChange,
  label,
}) => {
  const [input, setInput] = useState(value);
  const debounced = useDebouncedValue(input, FILTER_DEBOUNCE_MS);
  const pushed = useRef(value);

  useEffect(() => {
    if (debounced === pushed.current) return;
    pushed.current = debounced;
    onChange(debounced);
  }, [debounced, onChange]);

  // External resets (Clear all / hydration) flow back down.
  useEffect(() => {
    if (value !== pushed.current) {
      pushed.current = value;
      setInput(value);
    }
  }, [value]);

  // FS-889: leaving the box commits at once, so a Save that blurs the editors
  // captures what was typed without waiting out the debounce.
  const commitNow = () => {
    if (input === pushed.current) return;
    pushed.current = input;
    onChange(input);
  };

  return (
    <Input
      type="text"
      value={input}
      onChange={(e) => setInput(e.target.value)}
      onBlur={commitNow}
      placeholder="contains…"
      className="border rounded-sm px-2 py-0.5 text-sm w-36"
      aria-label={`${label} contains`}
      title={`Show devices whose ${label} contains this text; clear it to include every value`}
    />
  );
};

/** Enum / entity-path multi-select fed by the server facet option list. */
const MultiSelectEditor: React.FC<{
  label: string;
  value: string;
  options: DeviceSearchFacet[];
  onChange: (v: string) => void;
  /** Client-side display name for option values without a server label. */
  optionLabel?: (value: string) => string;
}> = ({ label, value, options, onChange, optionLabel }) => {
  const [open, setOpen] = useState(false);
  const selected = new Set(value ? value.split(',') : []);

  const toggle = (v: string) => {
    const next = new Set(selected);
    if (next.has(v)) next.delete(v);
    else next.add(v);
    onChange([...next].join(','));
  };

  const display = (v: string, serverLabel?: string) =>
    serverLabel || (optionLabel ? optionLabel(v) : v);

  // Keep options the user already selected visible even if the current facet
  // list (computed under other filters) no longer contains them.
  const optionValues = new Set(options.map((o) => String(o.value)));
  const orphans = [...selected].filter((v) => !optionValues.has(v));

  const summary =
    selected.size === 0
      ? '(any)'
      : selected.size === 1
        ? display(
            [...selected][0],
            options.find((o) => String(o.value) === [...selected][0])?.label,
          )
        : `${selected.size} selected`;

  return (
    <Popover
      open={open}
      onClose={() => setOpen(false)}
      label={`${label} options`}
      panelClassName="w-72 max-h-72 overflow-y-auto p-2"
      trigger={
        <Button
          variant="secondary"
          onClick={() => setOpen((o) => !o)}
          className="max-w-56"
          aria-expanded={open}
          aria-label={`${label} options`}
          title={`Pick which ${label} values to show; devices with any picked value stay in the list`}
        >
          <span className="truncate">{summary}</span>
          <ChevronDown size={14} aria-hidden className="shrink-0" />
        </Button>
      }
    >
      {options.length === 0 && orphans.length === 0 && (
        <p className="text-sm text-muted-foreground px-1 py-0.5">No options</p>
      )}
      <ul>
        {options.map((o) => {
          const v = String(o.value);
          return (
            <li key={v}>
              <label className="flex items-center gap-2 text-sm px-1 py-0.5 rounded-sm hover:bg-gray-50 cursor-pointer">
                <Checkbox checked={selected.has(v)} onCheckedChange={() => toggle(v)} aria-label={`Include ${display(v, o.label)}`} title={`Include or exclude ${display(v, o.label)}`} />
                <span className="truncate flex-1">{display(v, o.label)}</span>
                <span className="text-muted-foreground shrink-0">{o.count}</span>
              </label>
            </li>
          );
        })}
        {orphans.map((v) => (
          <li key={v}>
            <label className="flex items-center gap-2 text-sm px-1 py-0.5 rounded-sm hover:bg-gray-50 cursor-pointer">
              <Checkbox checked onCheckedChange={() => toggle(v)} aria-label={`Include ${display(v)}`} title={`Include or exclude ${display(v)}`} />
              <span className="truncate flex-1">{display(v)}</span>
              <span className="text-muted-foreground shrink-0">0</span>
            </label>
          </li>
        ))}
      </ul>
    </Popover>
  );
};

/** Min/Max pair, either end open, debounced like the text editor. */
const NumberRangeEditor: React.FC<{
  label: string;
  value: string;
  onChange: (v: string) => void;
}> = ({ label, value, onChange }) => {
  const [initLo, initHi] = splitRange(value);
  const [lo, setLo] = useState(initLo);
  const [hi, setHi] = useState(initHi);
  const joined = joinRange(lo.trim(), hi.trim());
  const debounced = useDebouncedValue(joined, FILTER_DEBOUNCE_MS);
  const pushed = useRef(value);

  useEffect(() => {
    if (debounced === pushed.current) return;
    pushed.current = debounced;
    onChange(debounced);
  }, [debounced, onChange]);

  useEffect(() => {
    if (value !== pushed.current) {
      pushed.current = value;
      const [l, h] = splitRange(value);
      setLo(l);
      setHi(h);
    }
  }, [value]);

  // FS-889: leaving either box commits at once (see TextEditor).
  const commitNow = () => {
    if (joined === pushed.current) return;
    pushed.current = joined;
    onChange(joined);
  };

  const cls = 'border rounded-sm px-2 py-0.5 text-sm w-20';
  return (
    <span className="inline-flex items-center gap-1">
      <Input
        type="number"
        value={lo}
        onChange={(e) => setLo(e.target.value)}
        onBlur={commitNow}
        placeholder="min"
        className={cls}
        aria-label={`${label} minimum`}
        title={`Show devices with ${label} at or above this value; clear it to remove the lower bound`}
      />
      <span className="text-muted-foreground">–</span>
      <Input
        type="number"
        value={hi}
        onChange={(e) => setHi(e.target.value)}
        onBlur={commitNow}
        placeholder="max"
        className={cls}
        aria-label={`${label} maximum`}
        title={`Show devices with ${label} at or below this value; clear it to remove the upper bound`}
      />
    </span>
  );
};

/**
 * A `YYYY-MM-DD` value as a local date. Set through `setFullYear`, because the
 * Date constructor reads a year of 0–99 as 1900–1999: a saved report may hold
 * any year from 0001, and the editor has to show the year the search runs.
 */
const parseYmd = (s: string): Date | null => {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s);
  if (!m) return null;
  const d = new Date(0);
  d.setFullYear(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  d.setHours(0, 0, 0, 0);
  return d;
};

/** From/To date pair, either end open. */
const DateRangeEditor: React.FC<{
  label: string;
  value: string;
  onChange: (v: string) => void;
}> = ({ label, value, onChange }) => {
  const [from, to] = splitRange(value);

  const push = (f: string, t: string) => onChange(joinRange(f, t));

  const cls = 'border rounded-sm px-2 py-0.5 text-sm w-28';
  return (
    <span className="inline-flex items-center gap-1">
      <DatePicker
        selected={parseYmd(from)}
        onChange={(d: Date | null) => push(d ? format(d, 'yyyy-MM-dd') : '', to)}
        dateFormat="yyyy-MM-dd"
        placeholderText="from"
        isClearable
        className={cls}
        ariaLabelledBy={undefined}
        customInput={<input aria-label={`${label} from`} title={`Show devices with ${label} on or after this date; clear it to remove the lower bound`} className={cls} />}
      />
      <span className="text-muted-foreground">–</span>
      <DatePicker
        selected={parseYmd(to)}
        onChange={(d: Date | null) => push(from, d ? format(d, 'yyyy-MM-dd') : '')}
        dateFormat="yyyy-MM-dd"
        placeholderText="to"
        isClearable
        className={cls}
        customInput={<input aria-label={`${label} to`} title={`Show devices with ${label} on or before this date; clear it to remove the upper bound`} className={cls} />}
      />
    </span>
  );
};

